import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FORFEO_RELIABILITY_OBJECTIVES,
  FORFEO_SEVERITY_POLICY,
} from "../../lib/forfeoObservabilityContract";
import {
  fetchObservabilitySnapshot,
  type ObservabilitySnapshot,
  type ObservabilitySourceStatus,
} from "../../lib/observabilityReadModel";
import {
  evaluateObservabilitySnapshot,
  formatCandidateTarget,
} from "../../lib/observabilityEvaluation";

const WINDOWS = [
  { value: 15, label: "15 minutes" },
  { value: 60, label: "1 heure" },
  { value: 360, label: "6 heures" },
  { value: 1440, label: "24 heures" },
] as const;

const STATUS_LABELS: Record<ObservabilitySourceStatus, string> = {
  available: "Disponible",
  degraded: "Dégradée",
  unavailable: "Indisponible",
};

const STATUS_CLASSES: Record<ObservabilitySourceStatus, string> = {
  available: "bg-emerald-100 text-emerald-800",
  degraded: "bg-amber-100 text-amber-900",
  unavailable: "bg-red-100 text-red-800",
};

function formatDate(value: string) {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString("fr-CA");
}

export default function ObservabilityConsole() {
  const [windowMinutes, setWindowMinutes] = useState(60);
  const [snapshot, setSnapshot] = useState<ObservabilitySnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadSnapshot = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setSnapshot(await fetchObservabilitySnapshot(windowMinutes));
    } catch (loadError) {
      setSnapshot(null);
      setError(loadError instanceof Error ? loadError.message : "Snapshot indisponible.");
    } finally {
      setLoading(false);
    }
  }, [windowMinutes]);

  useEffect(() => {
    void loadSnapshot();
  }, [loadSnapshot]);

  const evaluation = useMemo(
    () => snapshot ? evaluateObservabilitySnapshot(snapshot) : null,
    [snapshot],
  );

  return (
    <section data-forfeo-observability-console className="space-y-6 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.18em] text-indigo-700">Lot 4D · observabilité</p>
          <h2 className="mt-1 text-xl font-black text-slate-950 sm:text-2xl">État des sources opérationnelles</h2>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
            Snapshot administrateur agrégé. Aucune ligne métier, donnée financière brute ou erreur SQL n’est affichée.
          </p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          <label className="text-xs font-black uppercase tracking-wide text-slate-600">
            Fenêtre
            <select
              value={windowMinutes}
              onChange={(event) => setWindowMinutes(Number(event.target.value))}
              className="mt-1 min-h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm font-bold normal-case text-slate-900 sm:w-40"
            >
              {WINDOWS.map((window) => <option key={window.value} value={window.value}>{window.label}</option>)}
            </select>
          </label>
          <button
            type="button"
            onClick={() => void loadSnapshot()}
            disabled={loading}
            className="min-h-11 self-end rounded-xl bg-slate-950 px-4 text-sm font-black text-white disabled:opacity-50"
          >
            {loading ? "Chargement…" : "Actualiser"}
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
        <p className="font-black">Baseline et alertes inactives</p>
        <p className="mt-1 leading-6">Les objectifs sont encore candidats. Une disponibilité historique ne sera calculée qu’après une période mesurée suffisante.</p>
      </div>

      {error ? (
        <div role="alert" className="flex flex-col gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 sm:flex-row sm:items-center sm:justify-between">
          <span className="font-semibold">{error}</span>
          <button type="button" onClick={() => void loadSnapshot()} className="min-h-10 rounded-xl border border-red-300 px-4 font-black">Réessayer</button>
        </div>
      ) : null}

      {snapshot && evaluation ? (
        <div data-forfeo-observability-snapshot className="space-y-5">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <article className="rounded-2xl bg-slate-950 p-4 text-white">
              <p className="text-xs font-black uppercase tracking-wide text-slate-300">Évaluation locale</p>
              <p className="mt-2 text-lg font-black">{evaluation.label}</p>
              <p className="mt-1 text-xs leading-5 text-slate-300">{evaluation.detail}</p>
            </article>
            <article className="rounded-2xl bg-emerald-50 p-4">
              <p className="text-xs font-black uppercase tracking-wide text-emerald-700">Disponibles</p>
              <p className="mt-2 text-3xl font-black text-emerald-950">{snapshot.summary.available}</p>
            </article>
            <article className="rounded-2xl bg-amber-50 p-4">
              <p className="text-xs font-black uppercase tracking-wide text-amber-700">Dégradées</p>
              <p className="mt-2 text-3xl font-black text-amber-950">{snapshot.summary.degraded}</p>
            </article>
            <article className="rounded-2xl bg-red-50 p-4">
              <p className="text-xs font-black uppercase tracking-wide text-red-700">Indisponibles</p>
              <p className="mt-2 text-3xl font-black text-red-950">{snapshot.summary.unavailable}</p>
            </article>
          </div>

          <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs font-semibold text-slate-500">
            <span>Généré : {formatDate(snapshot.generated_at)}</span>
            <span>Fenêtre : {snapshot.window_minutes} min</span>
            <span>Corrélation : {snapshot.correlation_id}</span>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-slate-200">
            <table className="min-w-[760px] w-full text-left text-sm">
              <thead className="bg-slate-100 text-xs font-black uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">État</th>
                  <th className="px-4 py-3">Total agrégé</th>
                  <th className="px-4 py-3">Fenêtre</th>
                  <th className="px-4 py-3">Code normalisé</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {snapshot.sources.map((source) => (
                  <tr key={source.key}>
                    <td className="px-4 py-3"><p className="font-black text-slate-950">{source.label}</p><p className="mt-1 text-xs text-slate-500">{source.key}</p></td>
                    <td className="px-4 py-3"><span className={`inline-flex rounded-full px-3 py-1 text-xs font-black ${STATUS_CLASSES[source.status]}`}>{STATUS_LABELS[source.status]}</span></td>
                    <td className="px-4 py-3 font-black text-slate-800">{source.total_count ?? "—"}</td>
                    <td className="px-4 py-3 font-black text-slate-800">{source.recent_count ?? "—"}</td>
                    <td className="px-4 py-3 text-xs font-semibold text-slate-500">{source.error_code || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : loading ? (
        <div role="status" className="rounded-2xl bg-slate-50 p-8 text-center text-sm font-semibold text-slate-500">Chargement du snapshot sécurisé…</div>
      ) : null}

      <div data-forfeo-observability-objectives className="space-y-3">
        <div>
          <h3 className="text-lg font-black text-slate-950">Objectifs candidats</h3>
          <p className="mt-1 text-sm text-slate-600">Ces cibles ne sont pas encore des engagements actifs.</p>
        </div>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {FORFEO_RELIABILITY_OBJECTIVES.map((objective) => (
            <article key={objective.id} className="rounded-2xl border border-slate-200 p-4">
              <p className="text-xs font-black uppercase tracking-wide text-indigo-700">{formatCandidateTarget(objective.candidateTarget)}</p>
              <h4 className="mt-2 font-black text-slate-950">{objective.id}</h4>
              <p className="mt-2 text-xs leading-5 text-slate-600">Fenêtre {objective.windowDays} jours · baseline minimale {objective.minimumBaselineDays} jours</p>
              <span className="mt-3 inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-xs font-black text-slate-700">Baseline requise</span>
            </article>
          ))}
        </div>
      </div>

      <div data-forfeo-observability-severity className="overflow-x-auto rounded-2xl border border-slate-200">
        <table className="min-w-[760px] w-full text-left text-sm">
          <thead className="bg-slate-950 text-xs font-black uppercase tracking-wide text-white">
            <tr><th className="px-4 py-3">Niveau</th><th className="px-4 py-3">Déclencheur</th><th className="px-4 py-3">Accusé</th><th className="px-4 py-3">Mise à jour</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-200 bg-white">
            {FORFEO_SEVERITY_POLICY.map((severity) => (
              <tr key={severity.id}>
                <td className="px-4 py-3"><p className="font-black text-slate-950">{severity.id}</p><p className="text-xs text-slate-500">{severity.label}</p></td>
                <td className="max-w-xl px-4 py-3 text-slate-700">{severity.trigger}</td>
                <td className="px-4 py-3 font-black text-slate-800">{severity.acknowledgementMinutes} min</td>
                <td className="px-4 py-3 font-black text-slate-800">{severity.updateMinutes} min</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
